
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy, inject } from "@angular/core";
import { TaskService } from "../task.service";
import { select, Store } from "@ngrx/store";
import { LoadTasks, LoadTasksSuccess, getIsTasksNextPageLoading, getIsTasksIsMoreTasks, getTasksCurrentPageOffset, NextLoadTasks, NextLoadTasksSuccess, getSelectedFilterOption, getIsSearchMode, getSortBy, LoadTasksFail, NextLoadTasksFail, StoreProjectList, StorePriorityList, StoreMemberList, CreateTask, CreateTaskSuccess, CreateTaskFail, RemoveTasksSuccess, RemoveTasksFail, UpdateTask, UpdateTasksSuccess, UpdateTaskFail, UpdateTaskSuccess, StoreAuthUser, getAuthUser, RemoveTasks, AllowLoadMore, CreateTasksSuccess, SetTaskStatisticsInfo, SetIsLoading, getSelectedFilterType, StoreTagList, getTagList, SetSearchStatisticsInfo, SetTagStatisticsInfo, UpdateReminderTasks, getReminderTasks, StoreFolderList, getFolderList, SetListStatisticsInfo, StoreMyTeamUsers, StoreSaveSearches, StoreLocationList, SetLocationStatisticsInfo, getLocationList, getAssignmentTasks, UpdateAssignmentTasks, getMemberList, SetTaskDetailHighlight, getTasks, SetTaskFilterType, SetTaskFilterOption, StoreSettings, getTasksById, getTaskStatisticsInfo, SetSelectAll, ResetSelectedTaskId, SetDetailView, SetEditTaskView, LoadSettings, LoadPriorityList, LoadSaveSearches, FetchTasks, FetchMoreTasks } from "../store";
import { Task } from "../models/task";
import { TaskAttachment } from "../models/task-attachment";
import { TaskComment } from "../models/task-comment";
import { TasksRootState } from "../store/reducers/index";
import { TaskUtils } from "../shared/task-utils";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { TasksConstants } from "../shared/task-constacts";
import { Observable , of } from "rxjs";
import { SuccessService } from "../../common/providers/success-service";
import { ErrorService } from "../../common/providers/error-service";
import { TranslateService } from "@ngx-translate/core";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { ConfigService } from "../../common/providers/config.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { MessageTranslatorService } from "../services/message-translator-service";
import { LocalStorageService } from "../services/local-storage.service";
import { Subject, BehaviorSubject } from "rxjs";
import { DatabaseService } from "../services/database.service";
import { FilesStorageService } from "../services/files-storage.service";
import { CableService } from "../services/cable.service";
import { CommonUtil, MediaType, mobileonly } from "../../common/utils/common.utils";
import { AppConstants } from "../../common/utils/app-constants";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import { TaskNotificationType } from "../components/task-notifications";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { Logout } from "../../actions/app";
import { List } from "../models/list";
import { VncLibraryService } from "@uxf/vnc-library";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "../models/location";
import { taskAdapter } from "../store/reducers/tasks.reducers";
import { takeUntil, distinctUntilChanged, debounceTime, take, filter, tap, delay, catchError, switchMap, finalize, takeWhile, map } from "rxjs/operators";
import { TaskWatchersComponent } from "../components";
import { DomSanitizer } from "@angular/platform-browser";
import { ElectronService } from "../services/electron.service";
import { ContactInfo } from "src/app/shared/models/contact-info.model";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";

import { TaskRepositoryStateBase } from "./task.repository-state";


/**
 * Execution engine for the task flows (STR-07).
 *
 * Every method here used to live inside TaskRepository. The flow bodies (perform*) are
 * invoked exclusively by TaskFlowEffects in response to dispatched request actions; the
 * shared projection helpers (params/search, avatar assignment, DB projection, store
 * placement) are used both by those flows and - via one-line delegates - by the engines
 * that stayed in the repository (sync, polling, offline replay).
 *
 * The bodies were moved verbatim. This class mirrors the repository state-base member
 * names (store, taskService, databaseService, msgs, isOnline, authUser, ...) so the moved
 * code compiles unchanged; searchParams and FormattedDateForDB live HERE now, and the
 * repository exposes delegating accessors so components and the replay keep working.
 *
 * Chained flows that remain repository engines (getTasksStats, getDataFromDatabase,
 * processGetAllTasksResultToDataBase) are reached through the chain registered by
 * TaskRepository at construction - see registerChain.
 *
 * DI: provided in root AND re-provided in TaskModule, deliberately matching the existing
 * pattern for TaskRepository/TaskService/DatabaseService, so the module world and the
 * root world each stay internally consistent (see the STR-07 search-regression note).
 */
@Injectable({
  providedIn: "root"
})
export class TaskFlowService {
  private taskService = inject(TaskService);
  private store = inject<Store<TasksRootState>>(Store);
  private errorService = inject(ErrorService);
  private successService = inject(SuccessService);
  private configService = inject(ConfigService);
  private broadcaster = inject(Broadcaster);
  private localStorageService = inject(LocalStorageService);
  private databaseService = inject(DatabaseService);
  private messageTranslatorService = inject(MessageTranslatorService);
  private vncLibaryService = inject(VncLibraryService);
  private sanitizer = inject(DomSanitizer);

  /** Mirrors of the repository state the moved bodies read. */
  private msgs: any = {};
  private isOnline: boolean = false;
  private authUser: any;
  private isCordovaOrElectron = environment.isCordova || environment.isElectron;

  /** Search query params, set by the header/sidebar via the repository accessors. */
  public searchParams: any;
  /** Raw date the bulk-edit dialog stages for the DB projection. */
  public FormattedDateForDB: Date;

  /**
   * The repository instance whose engine flows (stats, DB pulls) the moved bodies chain
   * into. Registered by TaskRepository's constructor; typed loosely to avoid a DI cycle.
   */
  private chainRepo: any;

  constructor() {
    this.messageTranslatorService.translatedMessagesd$.subscribe(res => {
      this.msgs = res;
    });
    this.store.select(getOnlineStatus).subscribe(isOnline => {
      this.isOnline = isOnline;
    });
    this.store.select(getAuthUser).subscribe(user => {
      if (user) {
        this.authUser = user;
      }
    });
  }

  public registerChain(repo: any): void {
    this.chainRepo = repo;
  }

  public setSearchParams(searchParams: any): void {
    this.searchParams = searchParams;
  }

  public getSearchParamsValue(): any {
    return this.searchParams;
  }

  // Chained repository engine flows (see class doc).
  public getTasksStats(): void {
    this.chainRepo.getTasksStats();
  }

  public getDataFromDatabase(taskFilter): void {
    this.chainRepo.getDataFromDatabase(taskFilter);
  }

  public processGetAllTasksResultToDataBase(tasks: Task[], defaultFilter?): void {
    this.chainRepo.processGetAllTasksResultToDataBase(tasks, defaultFilter);
  }

  public getTagsListWithCounters(): void {
    this.chainRepo.getTagsListWithCounters();
  }

  public getFolderListWithCounters(): void {
    this.chainRepo.getFolderListWithCounters();
  }

  public getLocationsWithCounters(): void {
    this.chainRepo.getLocationsWithCounters();
  }

  public updateTaskIntoStore(oldTask: Task, newTask: Task) {
    let routingOption;
    let routingType;
    let sortBy;
    let authUser: AuthUser = null;
    this.store.select(getAuthUser).pipe(take(1)).subscribe( user => authUser = user );
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);

    let newTasks = [newTask];

    this.poppulateAssigneeAvatar(newTasks).subscribe((success) => {
      if (success) {
        const isUserWatcher = newTask.watchers && newTask.watchers.some(watcher => watcher.id === authUser?.id);
        if (( authUser  && authUser.id !== newTask.author.id ) && ( newTask.assigned_to && authUser.id !== newTask.assigned_to.id) && ( routingOption !== TasksConstants.ROUTE_TASKS_I_WATCH) && !isUserWatcher) {
          this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
        } else {
          if (routingType === TasksConstants.ROUTE_TYPE_STATUS) {
            switch (routingOption) {
              case TasksConstants.ROUTE_COMPLETED:
                if (newTask.status.id !== TaskUtils.statusCompletedId()) {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                } else {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                }
                break;
              case TasksConstants.ROUTE_OPEN:
                if (newTask.status.id === TaskUtils.statusCompletedId()) {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                } else {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                }
                break;
              case TasksConstants.ROUTE_NEW:
                if (newTask.status.id !== TaskUtils.statusNewId()) {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                } else {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                }
                break;
              case TasksConstants.ROUTE_SEARCH:
                let searchParams = this.getSearchQuery();
                if (searchParams["v[subject][]"] && newTask.subject.toLowerCase().indexOf(searchParams["v[subject][]"][0].toLowerCase()) === -1) {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                } else {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                }
                break;
              case TasksConstants.ROUTE_ASSIGNEDTOME:
                if (oldTask.assigned_to.id !== newTask.assigned_to.id || ( authUser  && authUser.id === newTask.author.id )) {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                } else {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                }
                break;
              case TasksConstants.ROUTE_TODAY_DUE:
                if (newTask.due_date) {
                  let taskDate = newTask.due_date;
                  taskDate.setHours(0, 0, 0, 0);
                  if (taskDate.getTime() === TaskUtils.getCurrentDate().getTime()) {
                    this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                  } else {
                    this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                  }
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                break;
              case TasksConstants.ROUTE_TOMORROW_DUE:
                if (newTask.due_date) {
                  let taskDate = newTask.due_date;
                  taskDate.setHours(0, 0, 0, 0);
                  if (taskDate.getTime() === TaskUtils.getTomorrowDate().getTime()) {
                    this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                  } else {
                    this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                  }
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                break;
              case TasksConstants.ROUTE_THIS_WEEK_DUE:
                if (newTask.due_date) {
                  let taskDate = newTask.due_date;
                  taskDate.setHours(0, 0, 0, 0);
                  let weekDate = TaskUtils.getstartAndEndOfWeek();
                  if (taskDate.getTime() >= weekDate[0].getTime() && taskDate.getTime() <= weekDate[1].getTime()) {
                    this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                  } else {
                    this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                  }
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                break;
              case TasksConstants.ROUTE_TASKS_I_WATCH:
                if (newTask.watchers) {
                  let watcher = newTask.watchers.find( watcher => watcher.id === authUser.id );
                  if (watcher) {
                    this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                  } else {
                    this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                  }
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                break;
              case TasksConstants.ROUTE_MY_OVERDUE_TASKS:
                if (newTask.due_date) {
                  let taskDate = newTask.due_date;
                  taskDate.setHours(0, 0, 0, 0);
                  if ((taskDate.getTime() < TaskUtils.getCurrentDate().getTime()) && ( newTask.status.id !== TaskUtils.statusCompletedId())) {
                    this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                  } else {
                    this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                  }
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                break;
              default:
                this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
                break;
            } // end switch

            if (!_.isEqual(newTask.tags, oldTask.tags)) {
              this.getTagsListWithCounters();
            }
            if (!_.isEqual(newTask.list, oldTask.list)) {
              this.getFolderListWithCounters();
            }
            if (!_.isEqual(newTask.location, oldTask.location)) {
              this.getLocationsWithCounters();
            }

          } else if (routingType === TasksConstants.ROUTE_TYPE_TAG) {
            if (newTask.tags) {
              if (_.isEqual(newTask.tags, oldTask.tags)) {
                this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
              } else {
                let tag = newTask.tags.find( tag => tag.name === routingOption);
                if (tag) {
                  this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: { ...newTasks[0] } } }));
                } else {
                  this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
                }
                this.getTagsListWithCounters();
              }
            } else {
              this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
            }
          } else if ( routingType === TasksConstants.ROUTE_TYPE_LIST) {
            if (newTask.list && newTask.list.id === oldTask.list.id ) {
              this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
            } else {
              this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
            }
          } else if ( routingType === TasksConstants.ROUTE_TYPE_LOCATION) {
            if (newTask.location && newTask.location.id === oldTask.location.id ) {
              this.store.dispatch(new UpdateTaskSuccess({ task: { id: newTasks[0].id, changes: newTasks[0] } }));
            } else {
              this.store.dispatch(new RemoveTasksSuccess({ ids: [newTasks[0].id] }));
            }
          }
        }
      }
    });
  }

  public pushUpdateTaskSuccessMessage(taskId?) {
    this.successService.emit({ id: SuccessType.TaskUpdated, messages: this.msgs.TASK_UPDATED ,taskId:taskId});
  }

  /** The load flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performGetTasks(defaultTaskParams?) {
    let params = this.getParams(defaultTaskParams);
    const offsetZero = 0;
    let isSearchMode;
    this.store.select(getIsSearchMode).pipe(take(1)).subscribe(o => isSearchMode = o);
    if (params) {
      this.store.dispatch(new LoadTasks());
      // load tasks from SQLite for the mobile client
      if (CommonUtil.isSQLSupported()) {
        if (isSearchMode && this.isOnline) {
          this.taskService.getTasks(offsetZero, AppConstants.TASKS_LIMIT, params).pipe(take(1))
          .subscribe(tasks => {
            this.processGetTasksResult(tasks);
          }, err => {
            this.store.dispatch(new LoadTasksFail());
            this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
          });
        } else {
          this.databaseService
          .getTasks(offsetZero, AppConstants.TASKS_LIMIT, params)
          .subscribe(tasks => {
              if (this.isOnline && tasks.length < AppConstants.TASKS_LIMIT) {
                this.processGetTasksResult(tasks);
                this.maybeLoadRemainingTasksFromServer(params);
              } else {
                this.processGetTasksResult(tasks);
              }
          }, err => {
            this.store.dispatch(new LoadTasksFail());
            this.vncLibaryService.openSnackBar(err, "","", "", 3000, "bottom", "left").subscribe(res => {});
          });
        }
      // load tasks from Server
      } else {
        this.taskService.getTasks(offsetZero, AppConstants.TASKS_LIMIT, params).pipe(take(1))
          .subscribe(tasks => {
            if (defaultTaskParams) {
              this.processGetAllTasksResultToDataBase(tasks, defaultTaskParams);
            }
            else {
              this.processGetTasksResult(tasks);
            }
          }, err => {
            this.store.dispatch(new LoadTasksFail());
            this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
          }
        );
      }
    }
  }

  public maybeLoadRemainingTasksFromServer(params){
    console.log("[task.repository] maybeLoadRemainingTasksFromServer");
    this.store.dispatch(new LoadTasks());
    const offsetZero = 0;

    this.taskService.getTasks(offsetZero, AppConstants.TASKS_LIMIT, params).pipe(take(1))
      .subscribe(tasks => {
        if (tasks.length > 0) {
          // save to DB
          this.databaseService.saveOrUpdateTasks(tasks).subscribe((res) => {
            console.log("[task.repository] maybeLoadRemainingTasksFromServer. tasks count:", tasks.length);
            this.processGetTasksResult(tasks);
          });
        } else {
          this.processGetTasksResult(tasks);
        }
      }, err => {
        this.store.dispatch(new LoadTasksFail());
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      }
    );
  }

  public processGetTasksResult(tasks: Task[]) {
    console.log("[task.repository] processGetTasksResult. count", tasks.length);

    let offset = tasks.length;
    let isMoreTasks = false;
    if (tasks.length >= AppConstants.TASKS_LIMIT) {
      isMoreTasks = true;
    } else {
      offset = 0;
      isMoreTasks = false;
    }

    if (tasks.length > 0) {
      this.poppulateAssigneeAvatar(tasks).subscribe((success) => {
        if (success) {
          this.loadTaskToDb(tasks).subscribe(taskFromDB => {
            this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: taskFromDB }));
          });
        }
      });
    } else {
      this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
    }
  }

  /** The load-more flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performLoadMoreTasks() {
    let nextPageLoading: boolean;
    this.store.select(getIsTasksNextPageLoading).pipe(take(1)).subscribe(o => nextPageLoading = o);

    if (nextPageLoading) {
      return;
    }
    let currentOffset = 0;
    let isMoreTasks = false;
    this.store.select(getTasksCurrentPageOffset).pipe(take(1)).subscribe(o => currentOffset = o);
    this.store.select(getIsTasksIsMoreTasks).pipe(take(1)).subscribe(l => isMoreTasks = l);

    console.log("[task.repository] loadMoreTasks. isMoreTasks: " + isMoreTasks + ", currentOffset: " + currentOffset);

    if (!isMoreTasks) {
      return;
    }

          // of all tasks are loaded in offline -> let a user try to load more when online

    const params = this.getParams();
    if (params) {
      console.log("[task.repository] loadMoreTasks start loading");
      this.store.dispatch(new NextLoadTasks());

      // load more tasks from SQLite for the mobile client
      if (CommonUtil.isSQLSupported()) {
        this.databaseService.getTasks(currentOffset, AppConstants.TASKS_LIMIT, params)
          .subscribe(tasks => {
          if (this.isOnline && tasks.length < AppConstants.TASKS_LIMIT) {
            this.maybeLoadRemainingLoadMoreTasksFromServer(params, currentOffset);
          } else {
            this.processLoadMoreTasksResult(tasks, currentOffset);
          }
        }, err => {
          this.store.dispatch(new LoadTasksFail());
          this.vncLibaryService.openSnackBar(err, "","", "", 3000, "bottom", "left").subscribe(res => {});
        });

      // load more tasks from Server
      } else {
        if (this.isOnline) {
          this.taskService.getTasks(currentOffset, AppConstants.TASKS_LIMIT, params).pipe(take(1))
            .subscribe(tasks => {
              this.processLoadMoreTasksResult(tasks, currentOffset);
            }, err => {
              this.store.dispatch(new NextLoadTasksFail());
              this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
            });
        }
        else {
          let taskFilter = "";
          this.store.select(getSelectedFilterOption).subscribe(res => {
            taskFilter = res;
          });
          this.getMoreDataFromDatabase(taskFilter, currentOffset);
        }
      }
    }
  }

  public maybeLoadRemainingLoadMoreTasksFromServer(params, currentOffset: number){
    console.log("[task.repository] maybeLoadRemainingLoadMoreTasksFromServer. loadedFromDBTasks");

    this.taskService.getTasks(currentOffset, AppConstants.TASKS_LIMIT, params).pipe(take(1))
      .subscribe(tasks => {
        if (tasks.length > 0) {
          // save to DB
          this.databaseService.saveOrUpdateTasks(tasks).subscribe((res) => {
            console.log("[task.repository] maybeLoadRemainingLoadMoreTasksFromServer. mergedTasks count:", tasks.length);
            this.processLoadMoreTasksResult(tasks, currentOffset);
          });
        } else {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }
      }, err => {
        this.store.dispatch(new LoadTasksFail());
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      }
    );
  }

  public processLoadMoreTasksResult(tasks: Task[], offset){
    let isMoreTasks;
    let newOffset = offset + tasks.length;
    if (tasks.length >= AppConstants.TASKS_LIMIT) {
      isMoreTasks = true;
    } else {
      newOffset = 0;
      isMoreTasks = false;
    }

    console.log("[task.repository] processLoadMoreTasksResult. isMoreTasks: " + isMoreTasks + ". newOffset: " + newOffset + ". tasks count: " + tasks.length);

    this.poppulateAssigneeAvatar(tasks).subscribe((success) => {
      if (success) {
        this.loadTaskToDb(tasks).subscribe(taskFromDB => {
          // this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: taskFromDB }));
          this.store.dispatch(new NextLoadTasksSuccess({currOffset: newOffset, isMoreTasks: isMoreTasks, task: tasks as Task[]}));
        });
      }
    });
  }

  getMoreDataFromDatabase(taskFilter, currentOffset) {
    switch (taskFilter) {
      case "all":
        this.databaseService.getAllTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "open":
        this.databaseService.getOpenTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "new":
        this.databaseService.getNewTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "completed":
        this.databaseService.getCompletedTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "watch":
        this.databaseService.getWatchTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "overdue":
        this.databaseService.getOverdueTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "week":
        this.databaseService.getWeekTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "tomorrow":
        this.databaseService.getTomorrowTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "assigned":
        this.databaseService.getAssignedTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "today":
        this.databaseService.getTodayTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
      case "created":
        this.databaseService.getCreatedTasks().subscribe(tasks => {
          this.processLoadMoreTasksResult(tasks, currentOffset);
        }, err => {
          this.store.dispatch(new NextLoadTasksFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
        break;
    }
  }

  public getParams(defaultTaskParams?) {
    let params;

    let routingType;
    let routingOption;
    let isSearchMode;
    let sortBy;
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);
    this.store.select(getIsSearchMode).pipe(take(1)).subscribe(o => isSearchMode = o);
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);

    if (defaultTaskParams) {
      routingType = defaultTaskParams.routingType,
      routingOption = defaultTaskParams.routingOption;
    }

    if (isSearchMode) {
      params = this.getSearchQuery();
    } else {
      if (routingType === TasksConstants.ROUTE_TYPE_STATUS) {
        params = TaskUtils.getParamsForFilters(routingOption, sortBy);
      } else if (routingType === TasksConstants.ROUTE_TYPE_TAG){
        this.store.select(getTagList).pipe(take(1)).subscribe(tags => {
          if (tags.length > 0) {
            let tag = tags.find( tag => tag.name === routingOption);
            if (tag) {
              params = TaskUtils.getParamsForTags(tag.id, sortBy);
            }
          }
        });
      } else if (routingType === TasksConstants.ROUTE_TYPE_LIST) {
        this.store.select(getFolderList).pipe(take(1)).subscribe(lists => {
          if (lists.length > 0) {
            let list = lists.find( list => list.name === routingOption);
            if (list) {
              params = TaskUtils.getParamsForLists(list.id, sortBy);
            }
          }
        });
      } else {
        this.store.select(getLocationList).pipe(take(1)).subscribe(locations => {
          if (locations.length > 0) {
            let location = locations.find( location => location.name === routingOption);
            if (location) {
              params = TaskUtils.getParamsForLocations(location.id, sortBy);
            }
          }
        });
      }
    }
    return params;
  }

  public getSearchQuery() {
    let query: any = Object.assign({}, this.searchParams, {});

    // author or assigned
    let key = "author_or_assigned";
    query[`op[${key}]`] = "=";

    if (!query["f[]"]) {
      query["f[]"] = [];
    }
    query["f[]"].push(key);

    if (!query[`v[${key}][]`]) {
      query[`v[${key}][]`] = "me";
    }

    key = "project_id";
    if (!query["op[project_id]"]) {
      if (!query["f[]"]) {
        query["f[]"] = [];
      }
      query["f[]"].push(key);
      query[`op[${key}]`] = "=";
      if (!query[`v[${key}][]`]) {
        query[`v[${key}][]`] = "mine";
      }
    }

    let sortBy;
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);

    let order = sortBy === TasksConstants.PRIORITY_SORTING_KEY ? TasksConstants.DESC : TasksConstants.ASC;
    query["sort"] = sortBy + ":" + order;

    return Object.keys(query).length === 1 ? {} : query;
  }

  loadTaskToDb(task, defaultFilter?): Observable<any> {
    const response = new BehaviorSubject<any>([]);
    let routingType = "";
    let taskFilter = "";
    this.store.select(getSelectedFilterOption).subscribe(res => {
      taskFilter = res;
    });
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
    if (defaultFilter) {
      if (defaultFilter.routingType === "status") {
        taskFilter = defaultFilter.routingOption;
      }
      if (defaultFilter.routingType === "list") {
        routingType = defaultFilter.routingType;
        taskFilter = defaultFilter.routingOption;
      }
      if (defaultFilter.routingType === "tags") {
        routingType = defaultFilter.routingType;
        taskFilter = defaultFilter.routingOption;
      }
      if (defaultFilter.routingType === "location") {
        routingType = defaultFilter.routingType;
        taskFilter = defaultFilter.routingOption;
      }
    }
    if (routingType === "status") {
      if (taskFilter === "all") {
        this.databaseService.addAllTasks(task);
        task.map(item => {
          try {
            CommonUtil.getBase6ImageFromUrl(item?.userAvatar).subscribe(res => {
              const avatar = {
                id: item.assigned_to.id,
                data: res,
                jid: item.assigned_to.jid
              };
              this.databaseService.addAvatar(avatar);
            }, () => {});
          } catch (error) {}
          if (item?.comments?.length) {
            try {
              item.comments.map(item => {
                CommonUtil.getBase6ImageFromUrl(item?.userAvatar).subscribe(res => {
                  const avatar = {
                    id: item.user.id,
                    data: res
                  };
                  this.databaseService.addAvatar(avatar);
                }, () => {});
              });
            } catch (error) {}
          }
        });
        this.getContactInfo(task);
      }
      if (taskFilter === "open") {
        this.databaseService.addOpenTasks(task);
      }
      if (taskFilter === "new") {
        this.databaseService.addNewTasks(task);
      }
      if (taskFilter === "completed") {
        this.databaseService.addCompletedTasks(task);
      }
      if (taskFilter === "assigned") {
        this.databaseService.addAssignedTasks(task);
      }
      if (taskFilter === "created") {
        this.databaseService.addCreatedTasks(task);
      }
      if (taskFilter === "today") {
        this.databaseService.addTodayTasks(task);
      }
      if (taskFilter === "tomorrow") {
        this.databaseService.addTomorrowTasks(task);
      }
      if (taskFilter === "week") {
        this.databaseService.addWeekTasks(task);
      }
      if (taskFilter === "watch") {
        this.databaseService.addWatchTasks(task);
      }
      if (taskFilter === "overdue") {
        this.databaseService.addOverdueTasks(task);
      }
    }
    if (routingType === "tags") {
      this.databaseService.addTagsTasks(taskFilter, task);
    }
    if (routingType === "list") {
      this.databaseService.addListTasks(taskFilter, task);
    }
    if (routingType === "location") {
      this.databaseService.addLocationTasks(taskFilter, task);
    }
    response.next(task);
    return response.asObservable();
  }

  getContactInfo(task: any[]) {

    let currentUser;
    try {
      currentUser = JSON.parse(StorageService.getLocalItem("profileUser"));
    } catch (error) {

    }
    const emailList = [...new Set(task?.map(item => item?.assigned_to?.jid))];
    emailList.forEach((item: string) => {
      if (item == currentUser?.email) {
        this.taskService.getLoggedInUserContactInfo(currentUser.email).subscribe(data => {
          if (!data?.jid) {
            return;
          }
          this.databaseService.addContactInfo(data);
          try {
            CommonUtil.getBase6ImageFromUrl(data?.avatar).subscribe(baseImg => {
              const avatar = {
                id: data.user_id,
                data: baseImg,
                jid: data?.jid
              };
              this.databaseService.addAvatar(avatar);
            // The surrounding try/catch cannot catch this: the image error
            // arrives asynchronously. Handle it here so it is not reported as
            // an unhandled RxJS error.
            }, () => {});
          } catch (error) {}
        });
      }
      else {
        this.taskService.getContactInfo(item).subscribe(res => {
          if (!res?.jid) {
            return;
          }
          this.databaseService.addContactInfo(res);
          try {
            CommonUtil.getBase6ImageFromUrl(res?.avatar).subscribe(baseImg => {
              const avatar = {
                id: res.user_id,
                data: baseImg,
                jid: res?.jid || res?.email
              };
              this.databaseService.addAvatar(avatar);
            }, error => {
              console.log("caught avt error: ", error);
            });
          } catch (error) {}
        });
      }
    });
  }

  public addTaskInStore(task: Task) {
    let routingOption;
    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);
    let routingType;
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);

    switch (routingType) {
      case TasksConstants.ROUTE_TYPE_STATUS:
      switch (routingOption) {
        case TasksConstants.ROUTE_COMPLETED:
        case TasksConstants.ROUTE_SEARCH:
        case TasksConstants.ROUTE_TASKS_I_WATCH:
          this.store.dispatch(new SetIsLoading(false));
          break;
        case TasksConstants.ROUTE_ASSIGNEDTOME:
          if (task.assigned_to) {
            let authUser;
            this.store.select(getAuthUser).pipe(take(1)).subscribe(o => authUser = o);
            if (authUser.id !== task.assigned_to.id) {
              this.store.dispatch(new CreateTaskSuccess({ task: task }));
            } else {
              this.store.dispatch(new SetIsLoading(false));
            }
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
          break;
        case TasksConstants.ROUTE_TODAY_DUE:
          if (task.due_date) {
            let taskDate = task.due_date;
            taskDate.setHours(0, 0, 0, 0);
            if (taskDate.getTime() === TaskUtils.getCurrentDate().getTime()) {
              this.store.dispatch(new CreateTaskSuccess({ task: task }));
            } else {
              this.store.dispatch(new SetIsLoading(false));
            }
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
          break;
        case TasksConstants.ROUTE_TOMORROW_DUE:
          if (task.due_date) {
            let taskDate = task.due_date;
            taskDate.setHours(0, 0, 0, 0);
            if (taskDate.getTime() === TaskUtils.getTomorrowDate().getTime()) {
              this.store.dispatch(new CreateTaskSuccess({ task: task }));
            } else {
              this.store.dispatch(new SetIsLoading(false));
            }
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
          break;
        case TasksConstants.ROUTE_THIS_WEEK_DUE:
          if (task.due_date) {
            let taskDate = task.due_date;
            taskDate.setHours(0, 0, 0, 0);
            let weekDate = TaskUtils.getstartAndEndOfWeek();
            if (taskDate.getTime() >= weekDate[0].getTime() && taskDate.getTime() <= weekDate[1].getTime()) {
              this.store.dispatch(new CreateTaskSuccess({ task: task }));
            } else {
              this.store.dispatch(new SetIsLoading(false));
            }
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
          break;
        default:
          this.store.dispatch(new CreateTaskSuccess({ task: task }));
          break;
        }
        break;
      case TasksConstants.ROUTE_TYPE_LIST:
        if (task.list) {
          if (routingOption === task.list.name) {
            this.store.dispatch(new CreateTaskSuccess({ task: task }));
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
        } else {
          this.store.dispatch(new SetIsLoading(false));
        }
        break;
      case TasksConstants.ROUTE_TYPE_TAG:
        if (task.tags) {
          let tag = task.tags.find( tag => tag.name === routingOption);
          if (tag) {
            this.store.dispatch(new CreateTaskSuccess({ task: task }));
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
        } else {
          this.store.dispatch(new SetIsLoading(false));
        }
        break;
      case TasksConstants.ROUTE_TYPE_LOCATION:
        if (task.location) {
          if (routingOption === task.location.name) {
            this.store.dispatch(new CreateTaskSuccess({ task: task }));
          } else {
            this.store.dispatch(new SetIsLoading(false));
          }
        } else {
          this.store.dispatch(new SetIsLoading(false));
        }
        break;
      default:
        this.store.dispatch(new CreateTaskSuccess({ task: task }));
        break;
    }
  }

  public poppulateAssigneeAvatar(tasks: Task[]): Observable<boolean> {
    console.log("[TaskRepository][poppulateAssigneeAvatar], tasks: ", tasks.length);

    const response = new BehaviorSubject(null);

    if (!tasks || tasks.length === 0) {
      response.next(true);
    } else {
      if (CommonUtil.isSQLSupported()) {
        this.performAvatarsAssignement(tasks, false, "userAvatar", 2).subscribe(done => {
          console.log("[TaskRepository][poppulateAssigneeAvatar] DONE");
          if (environment.isElectron) {
            tasks.forEach(task => {
              if (task.userAvatar) {
                if (CommonUtil.isFileSystemUrl(task.userAvatar.toString())) {
                  task.userAvatar = this.sanitizer.bypassSecurityTrustUrl(task.userAvatar);
                }
              }
            });
          }
          response.next(true);
        });
      } else {
        const currentTimeStamp = new Date().getTime();
        tasks.map(task => {
          if (task.assigned_to && this.authUser && task.assigned_to.id === this.authUser.id) {
            task.userAvatar = this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
          }
        });
        console.log("[TaskRepository][poppulateAssigneeAvatar] DONE");
        response.next(true);
      }
    }

    return response.asObservable().pipe(take(2));
  }

  public performAvatarsAssignement(objects: any[], forceResync: boolean, avatarPropertyName: string, type: number): Observable<boolean> {
    console.log("[TaskRepository][performAvatarsAssignement]", objects.length);

    const response = new Subject<boolean>();

    const currentTimeStamp = new Date().getTime();

    const serverAvatarsUrls = [];
    const uniqueUsersIds = [];
    const userObjectsIndexes = {};

    objects.forEach((obj, idx) => {
      const uid = this.avatarObjectId(obj, type);

      if (uid > 0) {
        // collect unique users
        if (uniqueUsersIds.indexOf(uid) === -1) {
          uniqueUsersIds.push(uid);

          // collect server avatar urls
          const serverUrlAvatar = this.avatarObjectUrl(obj, type, forceResync, currentTimeStamp);
          serverAvatarsUrls.push(serverUrlAvatar);
        }

        // group users and they objects
        const grouppedObjects = userObjectsIndexes[uid];
        if (grouppedObjects) {
          grouppedObjects.push(idx);
        } else {
          userObjectsIndexes[uid] = [idx];
        }
      }
    });

    this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
      this.databaseService.assignAvatars(uniqueUsersIds, serverAvatarsUrls, forceResync, avatarPropertyName).subscribe(resultsHash => {
        // result <userId: avatarUrl>
        uniqueUsersIds.forEach(uid => {
          const avatarLocalUrl = resultsHash[uid];

          const objectsIndexes = userObjectsIndexes[uid];
          objectsIndexes.forEach(idx => {
            objects[idx][avatarPropertyName] = avatarLocalUrl;
          });
        });

        console.log("[TaskRepository][performAvatarsAssignement] DONE");
        response.next(true);
      });
    });

    return response.asObservable().pipe(take(1));
  }

  public avatarObjectId(objectToAssignAvatar: any, type: number): any {
    // user
    if (type === 1){
      return objectToAssignAvatar.id;
    // task
    } else  if (type === 2){
      return objectToAssignAvatar.assigned_to ? objectToAssignAvatar.assigned_to.id : 0;
    // comment
    } else  if (type === 3){
      return objectToAssignAvatar.user.id;
    }
  }

  public avatarObjectUrl(objectToAssignAvatar: any, type: number, forceResync: boolean, currentTimeStamp: number): any {
    // user
    if (type === 1){
      if (forceResync && this.authUser && objectToAssignAvatar.id === this.authUser.id) {
        return this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
      }
      if (this.authUser && objectToAssignAvatar.id === this.authUser.id) {
        return this.authUser.userAvatar;
      }
      if (objectToAssignAvatar.avatar) {
        return objectToAssignAvatar.avatar;
      } else if (objectToAssignAvatar.userAvatar) {
        return objectToAssignAvatar.userAvatar;
      } else {
        return null;
      }
    // task
    } else  if (type === 2){
      if (forceResync && this.authUser && objectToAssignAvatar.assigned_to && objectToAssignAvatar.assigned_to.id === this.authUser.id) {
        return this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
      }
      if (this.authUser && objectToAssignAvatar.assigned_to && objectToAssignAvatar.assigned_to.id === this.authUser.id) {
        return this.authUser.userAvatar;
      }
      return objectToAssignAvatar.userAvatar;
    // comment
    } else  if (type === 3){
      if (forceResync && this.authUser && objectToAssignAvatar.user && objectToAssignAvatar.user.id === this.authUser.id) {
        return this.buildAvatarUrl(this.authUser.jid, currentTimeStamp);
      }
      if (this.authUser && objectToAssignAvatar.user && objectToAssignAvatar.user.id === this.authUser.id) {
        return this.authUser.userAvatar;
      }
      return objectToAssignAvatar.userAvatar;
    }
  }

  public buildAvatarUrl(jid: string, timestamp: number) {
    let avatarName = CommonUtil.buildTargetHash(jid);
    let avatarVersion = "?ver=" + timestamp;
    const avUrl = this.configService.avatarServiceUrl + "/" + avatarName + ".jpg" + avatarVersion;
    return avUrl;
  }

  updateTaskSuccess(oldTask: Task, newTask: Task) {
    console.log("TaskRepository updateTaskSuccess");
    this.updateTaskIntoStore(oldTask, newTask);
    this.pushUpdateTaskSuccessMessage(oldTask?.id);
    this.broadcaster.broadcast("clearSelectedTask");
  }

  /** The create flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performAddNewTask(payload: any) {
    console.log("[task.repository][addNewTask] payload", payload);

    this.store.dispatch(new CreateTask());

    // add on server
    if (this.isOnline) {
      this.taskService.addNewTask(payload).subscribe(task => {
        this.addNewTaskOnDBIfNeededAndFireResult(task);
      }, err => {
        this.addNewTaskError(err);
      });
    // create task in offline
    } else if (CommonUtil.isSQLSupported()) {
      const taskToCreate = Task.taskToCreateOffline(payload);
      this.databaseService.addNewTask(taskToCreate).subscribe(res => {
        this.databaseService.addPendingTaskCreate(taskToCreate.id).subscribe(res => {

        });
        this.addNewTaskSuccess(taskToCreate);
      });
    }
  }

  public addNewTaskOnDBIfNeededAndFireResult(task: Task) {
    // add on DB
    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.addNewTask(task).subscribe(res => {
          this.addNewTaskSuccess(task);
        });
      });
    } else {
      this.addNewTaskSuccess(task);
    }
  }

  public addNewTaskSuccess(task: Task){
    console.log("[TaskRepository] addNewTaskSuccess");

    this.poppulateAssigneeAvatar([task]).subscribe((success) => {
      if (success) {
        this.addTaskInStore(task);
        this.pushCreateTaskSuccessMessage(task?.id);
      }
    });
  }

  public addNewTaskError(err){
    console.log("[TaskRepository] addNewTaskError", err);

    this.store.dispatch(new CreateTaskFail());
    this.errorService.emit({ id: ErrorType.TaskCreateFail, messages: err });
  }

  public pushCreateTaskSuccessMessage(taskId) {
    this.successService.emit({ id: SuccessType.TaskCreated, messages: this.msgs.TASK_CREATED,taskId: taskId });
  }

  /** The remove flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performRemoveTasks(selectedIds: number[], successEvent: SuccessType) {
    console.log("[TaskRepository] removeTasksInternal: ", selectedIds);

    this.store.dispatch(new RemoveTasks());

    // remove on server
    if (this.isOnline) {
      this.taskService.removeTasks(selectedIds).pipe(take(1)).subscribe(res => {
        // remove on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.removeTasks(selectedIds).subscribe(res => {
            this.removeTasksSuccess(selectedIds, successEvent);
          });
        } else {
          this.removeTasksSuccess(selectedIds, successEvent);
        }
      }, err => {
        // 404
        if (err === "Resource Not Found" && CommonUtil.isSQLSupported()) {
          // remove on DB if not founfd on server
          this.databaseService.removeTasks(selectedIds).subscribe(res => {
            this.removeTasksSuccess(selectedIds, successEvent);
          });
        } else {
          this.removeTasksError(err);
        }
      });

    // offline remove
    } else if (CommonUtil.isSQLSupported()) {
      // delete on DB and create a pending operation
      this.databaseService.removeTasks(selectedIds).subscribe(res => {
        // add to pending queue
        selectedIds.forEach(taskId => {
          this.databaseService.addPendingTaskDelete(taskId).subscribe(res => {

          });
        });

        this.removeTasksSuccess(selectedIds, successEvent);
      });
    } else if (!this.isOnline) {
      let taskStats = [];
      this.store.select(getTaskStatisticsInfo).subscribe(info => {
        if (info) {
          taskStats = info;
        }
      });
      const id = CommonUtil.newPendingOperationId();
      const task = {
        id: id,
        body: {
          tasks: selectedIds,
          operationType: "deleteTask",
        }
      };
      this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
        selectedIds.forEach((elem) => {
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem, store).subscribe(t => {
              if (t) {
                const currentStoreStat = TaskRepositoryStateBase.statWithStore.find(r => r.store === store);
                this.removeTasksSuccess(selectedIds, successEvent);
                this.databaseService.deleteBulkTasks(elem, store);
                taskStats = taskStats.map(stat => {
                  if (stat.type === currentStoreStat.stat) {
                    stat.info.completed_count = stat.info.completed_count - 1;
                    stat.info.total_count = stat.info.total_count - 1;
                  }
                  return stat;
                });
                this.databaseService.addTaskStats(taskStats).subscribe(done => {
                  this.getTasksStats();
                  let routingOption;
                  this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => {
                    const currentStore = TaskRepositoryStateBase.statWithStore.find(r => r.store === store);
                    if (currentStore?.store == routingOption && routingOption != "search") {
                      this.getDataFromDatabase(routingOption);
                    }
                  });
                });
              }
            });
          });
        });
      });
    }
  }

  public removeTasksSuccess(selectedIds: number[], successEvent: SuccessType){
    console.log("[TaskRepository] removeTasksSuccess");

    this.store.dispatch(new RemoveTasksSuccess({ ids: selectedIds }));
    this.successService.emit({ id: successEvent, messages: this.msgs.TASK_REMOVED_MSG });
  }

  public removeTasksError(err){
    console.log("[TaskRepository] removeTasksError", err);

    this.store.dispatch(new RemoveTasksFail());
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
  }

  /** The duplicate flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performDuplicateTasks(tasks: Task[]): Observable<any> {
    const response = new Subject<any>();
    console.log("[TaskRepository] duplicateTasks. tasks: ", tasks);

    const tasksPayloadToCreate: any[] = tasks.map(t => this.getTaskCopy(t));

    this.store.dispatch(new CreateTask());

    console.log("[TaskRepository] duplicateTasks. tasksPayloadToCreate: ", tasksPayloadToCreate);

    if (this.isOnline) {
      // create on server
      this.taskService.bulkCreateTasks(tasksPayloadToCreate).pipe(take(1)).subscribe(createdTasks => {
        // create on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.saveOrUpdateTasks(createdTasks).pipe(take(1)).subscribe((res) => {
            this.duplicateTasksSuccess(createdTasks);
          });
        } else {
          this.duplicateTasksSuccess(createdTasks);
          this.databaseService.addNewTasks(createdTasks);
          this.databaseService.addAllTasks(createdTasks);
          response.next("done");
        }
      }, err => {
        this.duplicateTasksError(err);
        response.next(err);
      });

    // offline diplicate
    } else if (CommonUtil.isSQLSupported()) {
      // copy tasks
      let dupTasks = [];
      tasksPayloadToCreate.forEach(tp => {
        let copyTask: Task = Task.taskToCreateOffline(tp);
        dupTasks.push(copyTask);
      });

      dupTasks.forEach(taskToCreate => {
        this.databaseService.addNewTask(taskToCreate).subscribe(res => {
          this.databaseService.addPendingTaskCreate(taskToCreate.id).subscribe(res => {
            this.duplicateTasksSuccess(dupTasks);
          });
        });
      });
    }
    if (!this.isOnline) {
      let taskStats = [];
      this.store.select(getTaskStatisticsInfo).subscribe(info => {
        if (info) {
          taskStats = info;
        }
      });
      const id = CommonUtil.newPendingOperationId();
      const task = {
        id: id,
        body: {
          tasks: tasks,
          operationType: "duplicateTask" ,
        }
      };
      this.databaseService.addPendingOperationTask([task]).subscribe(res => {
        let required_statuses = this.getStatusItems();
        tasks.forEach((item, index) => {
          // The queue key and the placeholder task id used to share one random number,
          // so this read as `id + index`. With a UUID key that would concatenate
          // strings; the placeholder id is a separate concern and stays numeric.
          item.id = Task.tempId();
          let newStatus;
          newStatus = required_statuses.find(s => {
            if (s.name.toLowerCase() === "new") {
              return { id: s.id, name: s.name };
            }
          });
          item.isChecked = false;
          item.status = {
            id: newStatus.id,
            name: newStatus.name
          };
          this.databaseService.addNewTasks([item]);
          this.databaseService.addAllTasks([item]);
          newStatus = required_statuses.find(s => {
            if (s.name.toLowerCase() === "in progress") {
              return { id: s.id, name: s.name };
            }
          });
          item.status = {
            id: newStatus.id,
            name: newStatus.name
          };
          this.databaseService.addOpenTasks([item]);

          taskStats = taskStats.map(stat => {
            if (stat.type == "new" || stat.type == "open" || stat.type == "all") {
              stat.info.completed_count = stat.info.completed_count + 1;
              stat.info.total_count = stat.info.total_count + 1;
            }
            return stat;
          });
        });
        this.duplicateTasksSuccess(tasks);
        this.store.dispatch(new SetSelectAll(false));
        this.store.dispatch(new ResetSelectedTaskId());
        this.store.dispatch(new SetEditTaskView(false));
        this.databaseService.addTaskStats(taskStats).subscribe(done => {
          this.getTasksStats();
          let routingOption;
          this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);
          if (routingOption && routingOption != "search") {
            this.getDataFromDatabase(routingOption);
          }
        });
      });
    }
    return response.asObservable();
  }

  duplicateTasksSuccess(createdTasks: Task[]){
    console.log("[TaskRepository] duplicateTasksSuccess");

    let routingOption;
    let sortBy;
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);
    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);

    this.poppulateAssigneeAvatar(createdTasks).subscribe((success) => {
      if (success) {
        if (routingOption !== TasksConstants.ROUTE_COMPLETED &&
            routingOption !== TasksConstants.ROUTE_TODAY_DUE  &&
            routingOption !== TasksConstants.ROUTE_TOMORROW_DUE &&
            routingOption !== TasksConstants.ROUTE_THIS_WEEK_DUE ) {

          this.store.dispatch(new CreateTasksSuccess({ tasks: createdTasks }));
          this.broadcaster.broadcast("sortTasksList");
        } else {
          this.store.dispatch(new SetIsLoading(false));
        }
        this.successService.emit({ id: SuccessType.TaskDuplicated, messages: this.msgs.DUPLICATE_TASK_CREATED });
      }
    });
  }

  public duplicateTasksError(err){
    console.log("[TaskRepository] duplicateTasksError", err);
    this.store.dispatch(new CreateTaskFail());
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
  }

  public getTaskCopy(t: any): any {
    let taskObj = {
      assigned_to_id: t.assigned_to !== undefined ? t.assigned_to.id : null,
      assigned_to_name: t.assigned_to !== undefined ? t.assigned_to.name : null,
      priority_id: t.priority !== undefined ? t.priority.id : null,
      priority_name: t.priority !== undefined ? t.priority.name : null,
      project_id: t.project !== undefined ? t.project.id : null,
      project_name: t.project !== undefined ? t.project.name : null,
      start_date: (((t.start_date !== undefined && t.start_date !== null ) && t.start_date.getTime() !== TaskUtils.dueDateOrStartDateNull().getTime()) ? TaskUtils.datePipe.transform(t.start_date, "yyyy-MM-dd") : null),
      subject: t.subject,
      author_id: t.author !== undefined ? t.author.id : null,
      author_name: t.author !== undefined ? t.author.name : null,
      tracker_id: t.tracker !== undefined ? t.tracker.id : null,
      list_id: (t.list ? t.list.id : null),
      external_url: t.external_url !== undefined ? t.external_url : null
    };
    for (let propName in taskObj) {
      if (taskObj[propName] === null || taskObj[propName] === undefined) {
        delete taskObj[propName];
      }
    }
    return taskObj;
  }

  /** The bulk-update flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performUpdateBulkTasks(selectedTasks: Task[], fieldToUpdate: BulkUpdateIssueType, args: BulkUpdateArgs): Observable<any>{
    const response = new Subject<any>();
    console.log("[TaskRepository] updateBulkTasks");

    const tasksIds: number[] = selectedTasks.map(t => t.id);

    let valueToUpdate;
    switch (fieldToUpdate) {
      case BulkUpdateIssueType.StartDate:
      case BulkUpdateIssueType.DueDate:
      case BulkUpdateIssueType.InviteTo:
        valueToUpdate = (args.value != null) ? args.value : "none";
        break;
      case BulkUpdateIssueType.Tags:
        valueToUpdate = [args.name];
        break;
      case BulkUpdateIssueType.Watchers:
        let watchers = JSON.parse(args.value);
        valueToUpdate = [];
        watchers.forEach ( member => {
          valueToUpdate.push(member.id);
        });
        break;
      default:
        valueToUpdate = args.id;
    }

    console.log("[TaskRepository] updateBulkTasks. fieldToUpdate: " + fieldToUpdate + ". valuesToUpdate: " + valueToUpdate);

    this.store.dispatch(new UpdateTask());
    selectedTasks.forEach((i) => {
      i.isChecked = false;
    });
    if (this.isOnline) {
      // update on server
      this.taskService.updateBulkTasks(tasksIds, fieldToUpdate, valueToUpdate).pipe(take(1)).subscribe(res => {
        this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
        this.syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
        this.FormattedDateForDB = null;
        response.next("done");
      // server error
      }, err => {
        console.error("[TaskRepository] updateBulkTasks, error from server: ", err);
        let msg = "";
        switch (fieldToUpdate) {
          case BulkUpdateIssueType.StartDate:
            msg = this.msgs.START_DATE_CHANGE_ERROR_MESSAGE;
            break;
          case BulkUpdateIssueType.DueDate:
            msg = this.msgs.TASK_DUE_DATE_UPDATE_FAIL;
            break;
          default:
            msg = err;
        }
        response.error(err);
        this.store.dispatch(new UpdateTaskFail());
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: msg });
      });
    } else if (CommonUtil.isSQLSupported()) {
      this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
    }
    // if (!this.isOnline) this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
    else if (!this.isOnline) {
      const id = CommonUtil.newPendingOperationId();
      const task = {
        id: id,
        body: {
          tasks: selectedTasks,
          fieldToUpdate: fieldToUpdate,
          valueToUpdate: args,
          operationType: BulkUpdateIssueType[fieldToUpdate],
        }
      };
      this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
        // this.store.dispatch(new SetIsLoading(false));
        this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
        this.store.dispatch(new SetSelectAll(false));
        this.store.dispatch(new ResetSelectedTaskId());
        this.store.dispatch(new SetEditTaskView(false));
        this.syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
        response.next("done");
      }, err => {
        response.error(err);
        this.store.dispatch(new UpdateTaskFail());
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    }
    return response.asObservable();
  }

  public updateBulkTasksOnDB(selectedTasks: Task[], fieldToUpdate: BulkUpdateIssueType, args: BulkUpdateArgs, valueToUpdate: any) {
    let routingOption;
    let routingType;
    let sortBy;
    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);

    let removeTasksFromSet = false;
    let updateTasksFromSet = false;
    let updateTasks: Task[] = [];
    let tasksIds: number[] = selectedTasks.map(t => t.id);
    let watchers: any = null;

    // updates local tasks objects
    switch (fieldToUpdate) {
      case BulkUpdateIssueType.AssignedTo:
        const item = { id: args.id, name: args.name };
        if (args?.avatar_url) {
          item["avatar_url"] = args.avatar_url;
        }
        if (args?.login) {
          item["login"] = args.login;
        }
        if (args?.jid) {
          item["jid"] = args.jid;
        }
        if (args?.username) {
          item["username"] = args.username;
        }
        selectedTasks.forEach(task => {
          task.assigned_to = item;
          task.invitee_email = null;
        });
        let authUser;
        this.store.select(getAuthUser).pipe(take(1)).subscribe(o => authUser = o);
        if (routingOption === TasksConstants.ROUTE_ASSIGNEDTOME || routingOption === TasksConstants.ROUTE_MY_OVERDUE_TASKS) {
          if (args.id !== authUser.id) {
            removeTasksFromSet = true;
          }
        } else if (routingOption !== TasksConstants.ROUTE_CREATEDBYME && routingOption !== TasksConstants.ROUTE_TASKS_I_WATCH){
          selectedTasks.forEach ( task => {
            if ((task.assigned_to && task.assigned_to.id !== authUser.id) && ( task.author && task.author.id !== authUser.id)) {
              removeTasksFromSet = true;
              updateTasksFromSet = true;
            } else {
              tasksIds = tasksIds.filter( id => id !== task.id );
              updateTasks.push(task);
            }
          });
        }
        break;

      // assign to external user
      case BulkUpdateIssueType.InviteTo:
        selectedTasks.forEach(task => {
          task.assigned_to = null;
          task.invitee_email = args.value;
          task.userAvatar = null;
        });
        if (routingOption === TasksConstants.ROUTE_ASSIGNEDTOME) {
          removeTasksFromSet = true;
        }
        break;

      case BulkUpdateIssueType.Status:
        if (routingOption === TasksConstants.ROUTE_OPEN || routingOption === TasksConstants.ROUTE_NEW || routingOption === TasksConstants.ROUTE_MY_OVERDUE_TASKS) {
          removeTasksFromSet = true;
        }
        selectedTasks.forEach(task => {
          task.status = { id: args.id, name: args.name };
          task.done_ratio = 100;
        });
        break;

      case BulkUpdateIssueType.Priority:
        selectedTasks.forEach(task => {
          task.priority = { id: args.id, name: args.name };
        });
        break;

      case BulkUpdateIssueType.Project:
        selectedTasks.forEach(task => {
          task.project = { id: args.id, name: args.name };
        });
        break;

      case BulkUpdateIssueType.StartDate:
        selectedTasks.forEach(task => {
          task.start_date = (args.value != null) ? new Date(args.value) : null;
        });
        break;
      case BulkUpdateIssueType.Tags:
        selectedTasks.forEach(task => {
          let hasTag = task.tags.filter(tag =>
            tag.id === args.id
          );
          if (hasTag.length < 1 ) {
            task.tags.push({ id: args.id, name: args.name });
          }
        });
        break;

      case BulkUpdateIssueType.List:
        selectedTasks.forEach(task => {
          task.list = { id: args.id, name: args.name };
        });
        if (routingType === TasksConstants.ROUTE_TYPE_LIST && routingOption !== args.name ) {
          removeTasksFromSet = true;
        }
        break;

      case BulkUpdateIssueType.Watchers:
        selectedTasks.forEach( task => {
          if (!task.watchers) { task.watchers = []; }
          watchers = JSON.parse(args.value);
          watchers.forEach ( member => {
              task.watchers.push(member);
          });
        });
        break;

      case BulkUpdateIssueType.Location:
        selectedTasks.forEach(task => {
          task.location = { id: args.id, name: args.name };
        });
        if (routingType === TasksConstants.ROUTE_TYPE_LOCATION && routingOption !== args.name ) {
          removeTasksFromSet = true;
        }
        break;

      case BulkUpdateIssueType.DueDate:
        selectedTasks.forEach(task => {
          task.due_date = (args.value != null) ? new Date(args.value) : null;
        });
        if (routingOption === TasksConstants.ROUTE_TODAY_DUE ||
          routingOption === TasksConstants.ROUTE_TOMORROW_DUE ||
          routingOption === TasksConstants.ROUTE_THIS_WEEK_DUE ||
          routingOption === TasksConstants.ROUTE_MY_OVERDUE_TASKS) {
          let itemDate = new Date(args.value);
          itemDate.setHours(0, 0, 0, 0);
          switch (routingOption) {
            case TasksConstants.ROUTE_TODAY_DUE:
              if (itemDate.getTime() !== TaskUtils.getCurrentDate().getTime()) {
                removeTasksFromSet = true;
              }
              break;
            case TasksConstants.ROUTE_TOMORROW_DUE:
              if (itemDate.getTime() !== TaskUtils.getTomorrowDate().getTime()) {
                removeTasksFromSet = true;
              }
              break;
            case TasksConstants.ROUTE_THIS_WEEK_DUE:
              let weekDate = TaskUtils.getstartAndEndOfWeek();
              if (itemDate.getTime() < weekDate[0].getTime() || itemDate.getTime() > weekDate[1].getTime()) {
                removeTasksFromSet = true;
              }
              break;
            case TasksConstants.ROUTE_MY_OVERDUE_TASKS:
              if (itemDate.getTime() >= TaskUtils.getCurrentDate().getTime()) {
                removeTasksFromSet = true;
              }
              break;
          }
        }
        break;
    } // end switch

    if (!removeTasksFromSet) {
      // TODO: probably need to update avatars on assignee changed only
      this.poppulateAssigneeAvatar(selectedTasks).subscribe((success) => {
        if (success) {
          const changes = selectedTasks.map(task => {
            return { id: task.id, changes: task };
          });
          this.store.dispatch(new UpdateTasksSuccess({ tasks: changes }));
        }
      });
    } else {
      this.store.dispatch(new RemoveTasksSuccess({ ids: tasksIds }));

      if (updateTasksFromSet) {
        if ( updateTasks && updateTasks.length > 0) {
          this.poppulateAssigneeAvatar(updateTasks).subscribe((success) => {
            if (success) {
              const changes = updateTasks.map(task => {
                return { id: task.id, changes: task };
              });
              this.store.dispatch(new UpdateTasksSuccess({ tasks: changes }));
            }
          });
        }
      }
    }

    // TODO: improve the following DB requests
    // do 1 req instead of for loop

    // here we have updated tasks objects
    // reflect changes to DB
    if (CommonUtil.isSQLSupported()) {
      if (fieldToUpdate === BulkUpdateIssueType.Status) {
        // update on DB
        if (valueToUpdate === TaskUtils.statusCompletedId()) {
          selectedTasks.forEach(task => {
            this.databaseService.completeTask(task).subscribe(res => {

            });
          });
        }
      } else if (fieldToUpdate === BulkUpdateIssueType.Priority) {
        selectedTasks.forEach(task => {
          this.databaseService.updatePriority(task, task.priority.id).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.StartDate) {
        selectedTasks.forEach(task => {
          this.databaseService.updateStartDate(task, task.start_date).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.DueDate) {
        selectedTasks.forEach(task => {
          this.databaseService.updateDueDate(task, task.due_date).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.Project) {
        selectedTasks.forEach(task => {
          this.databaseService.updateProjectId(task, task.project.id).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.Tags) {
        selectedTasks.forEach(task => {
          this.databaseService.updateTags(task, task.tags).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.List) {
        this.databaseService.updateList(selectedTasks, valueToUpdate).subscribe(res => {

        });
      } else if (fieldToUpdate === BulkUpdateIssueType.Location) {
        this.databaseService.updateLocation(selectedTasks, valueToUpdate).subscribe(res => {

        });
      } else if (fieldToUpdate === BulkUpdateIssueType.AssignedTo) {
        selectedTasks.forEach(task => {
          this.databaseService.updateAssignedTo(task, task.assigned_to.id).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.InviteTo) {
        selectedTasks.forEach(task => {
          this.databaseService.updateAssignedTo(task, 0).subscribe(res => {

          });
        });
      } else if (fieldToUpdate === BulkUpdateIssueType.Watchers) {
        selectedTasks.forEach(task => {
          this.databaseService.updateWatchers(task, valueToUpdate).subscribe(res => {

          });
        });
      }

      // add pendings
      if (!this.isOnline) {
        selectedTasks.forEach(task => {
          this.databaseService.addPendingTaskUpdate(task.id, null, null, watchers).subscribe(res => {

          });
        });
      }
    }

    // notify
    this.broadcaster.broadcast("sortTasksList");
    if (fieldToUpdate === BulkUpdateIssueType.Status) {
      if (selectedTasks?.length === 1) {
        const id = selectedTasks[0].id;
        this.successService.emit({ id: SuccessType.TaskBulkStatusUpdated, messages: this.msgs.TASK_UPDATED,taskId: id });
      }
      else {
        this.successService.emit({ id: SuccessType.TaskBulkStatusUpdated, messages: this.msgs.TASK_UPDATED });
      }
    } else if (fieldToUpdate === BulkUpdateIssueType.Watchers) {
      if (selectedTasks?.length === 1) {
        const id = selectedTasks[0].id;
        this.successService.emit({ id: SuccessType.TaskBulkWatcherUpdated, messages: this.msgs.TASK_UPDATED,taskId: id });
      }
      else {
        this.successService.emit({ id: SuccessType.TaskBulkWatcherUpdated, messages: this.msgs.TASK_UPDATED });
      }
    } else {
      if (selectedTasks?.length === 1) {
        const id = selectedTasks[0].id;
        this.successService.emit({ id: SuccessType.TaskBulkUpdated, messages: this.msgs.TASK_UPDATED,taskId: id });
      }
      else {
        this.successService.emit({ id: SuccessType.TaskBulkUpdated, messages: this.msgs.TASK_UPDATED });
      }
    }
  }

  syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate) {
    switch (fieldToUpdate) {
      case BulkUpdateIssueType.Priority:
        let priority = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          elem["priority"] = priority;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
      case BulkUpdateIssueType.DueDate:
        selectedTasks.forEach((elem) => {
          elem["due_date"] = this.FormattedDateForDB;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
      case BulkUpdateIssueType.StartDate:
        selectedTasks.forEach((elem) => {
          elem["start_date"] = this.FormattedDateForDB;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
      case BulkUpdateIssueType.Location:
        let location = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          elem["location"] = location;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        this.databaseService.getLocationTasks(args.name).subscribe(res => {
          if (res) {
            for (let i of selectedTasks) {
              const index = res.findIndex(t => t.id === i.id);
              if (index > -1) {
                res[index] = i;
              }
              else {
                res.push(i);
              }
            }
            this.databaseService.addLocationTasks(args.name, res).subscribe(r => {
            });
          }
        });
        break;
      case BulkUpdateIssueType.List:
        let list = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          elem["list"] = list;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        this.databaseService.getListTasks(args.name).subscribe(res => {
          if (res) {
            for (let i of selectedTasks) {
              const index = res.findIndex(t => t.id === i.id);
              if (index > -1) {
                res[index] = i;
              }
              else {
                res.push(i);
              }
            }
            this.databaseService.addListTasks(args.name, res).subscribe(r => {
            });
          }
        });
        break;
      case BulkUpdateIssueType.Tags:
        let tags = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          if (!Object.keys(elem).includes("tags")) {
            elem["tags"] = [];
          }
          const index = elem["tags"].findIndex(t => t.id == args.id);
          if (index > -1) {
            elem["tags"][index] = tags;
          }
          else {
            elem["tags"].push(tags);
          }
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        this.databaseService.getTagsTasks(args.name).subscribe(res => {
          if (res) {
            for (let i of selectedTasks) {
              const index = res.findIndex(t => t.id === i.id);
              if (index > -1) {
                res[index] = i;
              }
              else {
                res.push(i);
              }
            }
            this.databaseService.addTagsTasks(args.name, res).subscribe(r => {
            });
          }
        });
        break;
      case BulkUpdateIssueType.Watchers:
        selectedTasks.forEach((elem) => {
          elem["repeat"] = valueToUpdate;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
      case BulkUpdateIssueType.AssignedTo:
        let assigned_to = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          elem["assigned_to"] = assigned_to;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
      case BulkUpdateIssueType.Project:
        let project = {
          id: args.id,
          name: args.name
        };
        selectedTasks.forEach((elem) => {
          elem["project"] = project;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
        break;
    }
  }

  /** The recurring-update flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performUpdateBulkTasksRecurring(selectedTasks: Task[], type: BulkUpdateIssueType, item: BulkUpdateArgs) {
    this.store.dispatch(new UpdateTask());
    const selectedIds: number[] = selectedTasks.map(t => t.id);
    if (this.isOnline) {
      this.taskService.updateBulkTasksRecurring(selectedIds, type, item.value).pipe(take(1)).subscribe(res => {
        this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
      }, err => {
        this.store.dispatch(new UpdateTaskFail());
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else if (CommonUtil.isSQLSupported()) {
      this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
    } else if (!this.isOnline) {
      const id = CommonUtil.newPendingOperationId();
      const task = {
        id: id,
        body: {
          tasks: selectedIds,
          fieldToUpdate: type,
          valueToUpdate: item.value,
          operationType: BulkUpdateIssueType[type],
        }
      };
      this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
        this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
        this.store.dispatch(new SetEditTaskView(false));
        this.store.dispatch(new SetSelectAll(false));
        this.store.dispatch(new ResetSelectedTaskId());
        selectedTasks.forEach((elem) => {
          elem["repeat"] = item.value;
          elem.isChecked = false;
          TaskRepositoryStateBase.taskStoreList.forEach((store) => {
            this.databaseService.getTaskById(elem.id, store).subscribe(t => {
              if (t) {
                this.databaseService.addBulkTasks([elem], store);
              }
            });
          });
        });
      });
    }
  }

  public updateBulkTasksRecurringOnDB(selectedTasks: Task[], type: BulkUpdateIssueType, item: BulkUpdateArgs) {
    selectedTasks.forEach(task => {
      task.repeat = item.value;
    });

    // reflect changes to DB
    if (CommonUtil.isSQLSupported()) {
      selectedTasks.forEach(task => {
        this.databaseService.updateRepeat(task, task.repeat).subscribe(res => {

        });
      });
    }

    const changes = selectedTasks.map(task => {
      return { id: task.id, changes: task };
    });
    this.store.dispatch(new UpdateTasksSuccess({ tasks: changes }));
    if (selectedTasks?.length === 1) {
      this.successService.emit({ id: SuccessType.TaskBulkUpdated, messages: this.msgs.TASK_UPDATED ,taskId: selectedTasks[0].id});
    }
    else {
      this.successService.emit({ id: SuccessType.TaskBulkUpdated, messages: this.msgs.TASK_UPDATED});
    }
  }

  /** The detail-update flow body, unchanged. Called only by TaskFlowEffects (STR-07). */
  public performUpdateTaskDetail(oldTask: Task, newTask: Task, fileUploads: FileUpload[], watcherMembers: User[]) {
    console.log("TaskRepository updateTaskDetail: ", oldTask, newTask, fileUploads);

    let attachments = oldTask.attachments.filter( item =>
      !newTask.attachments.some( other => item.id === other.id)
    );
    this.deleteBulkAttachments(oldTask.id, attachments);

    this.performUpdateTask(oldTask, newTask, fileUploads, watcherMembers);
  }

  /** The task-update flow body, unchanged. Reached only via performUpdateTaskDetail (STR-07). */
  public performUpdateTask(oldTask: Task, newTask: Task, fileUploads: FileUpload[], watcherMembers: User[]): Observable<any> {
    this.store.dispatch(new UpdateTask());
    const response = new Subject<any>();
    let removedTagsIds: number[] = [];
    oldTask.tags.forEach(oldTag => {
      let tagRemoved = true;
      for (let newTag of newTask.tags) {
        if (oldTag.id === newTag.id) {
          tagRemoved = false;
          break;
        }
      }
      if (tagRemoved) {
        removedTagsIds.push(oldTag.id);
      }
    });

    // update on server
    if (this.isOnline) {
      this.taskService.updateTask(oldTask, newTask, fileUploads, watcherMembers).pipe(take(1)).subscribe(serverRes => {

        if (CommonUtil.isSQLSupported()) {
          if (fileUploads && fileUploads.length > 0) {
            // get the updated task details form server to get proper & complete attachment parameters
            this.taskService.getTaskDetails(oldTask.id).subscribe(updatedTaskFromServer => {
              // Set watcher members
              // updatedTaskFromServer.watchers = watcherMembers;
              // update on DB
              this.databaseService.updateTask(updatedTaskFromServer, removedTagsIds).subscribe((dbRes) => {
                this.updateTaskSuccess(oldTask, updatedTaskFromServer);
              });
            });
          } else {
            // Set watcher members
            // newTask.watchers = watcherMembers;
            // update on DB
            this.databaseService.updateTask(serverRes, removedTagsIds).subscribe((dbRes) => {
              this.updateTaskSuccess(oldTask, serverRes);
            });
          }
        } else {
          response.next("done");
          this.updateTaskSuccess(oldTask, serverRes);
          for (let store of TaskRepositoryStateBase.taskStoreList) {
            this.databaseService.getTaskById(oldTask.id, store).subscribe(t => {
              if (t) {
                newTask.isChecked = false;
                this.databaseService.addBulkTasks([newTask], store);
              }
            });
          }
        }
      }, err => {
        response.next("done");
        this.updateTaskError(oldTask, err);
      });

    // offline task update
    } else if (CommonUtil.isSQLSupported()) {
      // update on DB
      if (watcherMembers && watcherMembers.length > 0) {
        newTask.watchers = watcherMembers;
      }
      this.databaseService.updateTask(newTask, removedTagsIds).subscribe((dbRes) => {
        const fileUploadsValue = fileUploads && fileUploads.length > 0 ? fileUploads : null;
        const repeatValue = newTask.repeat !== "" ? newTask.repeat : null;
        const watcherMembersValue = watcherMembers && watcherMembers.length > 0 ? watcherMembers : null;
        this.databaseService.addPendingTaskUpdate(newTask.id, fileUploadsValue, repeatValue, watcherMembersValue).subscribe(res => {
          this.updateTaskSuccess(oldTask, newTask);
        });
      });
    }
    if (!this.isOnline) {
      newTask.isChecked = false;
      const task = [
        {
          id: oldTask.id,
          body: {
            oldTask: oldTask,
            newTask: newTask,
            fileUploads: fileUploads,
            watcherMembers: watcherMembers,
            operationType: "nonBulkUpdate"
          }
        }
      ];
      this.databaseService.addPendingOperationTask(task).subscribe(r => {
        let tasksStore;
        this.store.select(getTasks).subscribe(tasksFromStore => {
          tasksStore = tasksFromStore;
        });
        response.next("done");
        const index = tasksStore.findIndex(item => item.id === oldTask.id);
        tasksStore[index] = r;
        this.databaseService.addAllTasks([newTask]).subscribe(res => {
          this.loadTaskToDb(tasksStore).subscribe(taskFromDB => {
            this.updateTaskSuccess(oldTask, r);
            this.store.dispatch(new SetEditTaskView(false));
            this.store.dispatch(new SetSelectAll(false));
            this.store.dispatch(new ResetSelectedTaskId());
            if (oldTask?.tags?.length) {
              const tagList = oldTask?.tags.map(item => item.name);
              for (let tag of tagList) {
                this.databaseService.getTagsTasks(tag).subscribe(res => {
                  if (res) {
                    const taskIndex = res.findIndex(item => item.id === oldTask.id);
                    res[taskIndex] = newTask;
                    if (taskIndex > -1) {
                      this.databaseService.addTagsTasks(tag, res).subscribe(r => {
                      });
                    }
                  }
                });
              }
            }
            if (oldTask?.location) {
              const locationName = oldTask?.location?.name;
              if (locationName) {
                this.databaseService.getLocationTasks(locationName).subscribe(res => {
                  if (res) {
                    const taskIndex = res.findIndex(item => item.id === oldTask.id);
                    res[taskIndex] = newTask;
                    this.databaseService.addLocationTasks(locationName, res).subscribe(r => {
                    });
                  }
                });
              }
            }
            if (oldTask?.list) {
              const listName = oldTask?.list?.name;
              if (listName) {
                this.databaseService.getListTasks(listName).subscribe(res => {
                  if (res) {
                    const taskIndex = res.findIndex(item => item.id === oldTask.id);
                    if (taskIndex > -1) {
                      res[taskIndex] = newTask;
                      this.databaseService.addListTasks(listName, res).subscribe(r => {
                      });
                    }
                  }
                });
              }
            }
            let taskStats = [];
            this.store.select(getTaskStatisticsInfo).subscribe(info => {
              if (info) {
                taskStats = info;
              }
            });
            for (let store of TaskRepositoryStateBase.taskStoreList) {
              this.databaseService.getTaskById(oldTask.id, store).subscribe(t => {
                if (t) {
                  this.databaseService.addBulkTasks([newTask], store).subscribe(done => {
                    if (newTask.status.name !== oldTask.status.name) {
                      if (newTask.status.name.toLowerCase() == "completed" && store === "OpenTaskStore") {
                        this.databaseService.deleteBulkTasks(oldTask.id, store);
                        this.databaseService.addBulkTasks([newTask], "CompletedTaskStore");
                        for (let item of taskStats) {
                          if (item.type == "completed") {
                            item.info.completed_count = item.info.completed_count + 1;
                            item.info.total_count = item.info.total_count + 1;
                          }
                          if (item.type == "open") {
                            item.info.total_count = item.info.total_count - 1;
                          }
                        }
                        this.databaseService.addTaskStats(taskStats).subscribe(done => {
                          this.getTasksStats();
                        });
                      }
                      if (newTask.status.name.toLowerCase() == "in progress" && store === "CompletedTaskStore") {
                        this.databaseService.deleteBulkTasks(oldTask.id, store);
                        this.databaseService.addBulkTasks([newTask], "OpenTaskStore");
                        for (let item of taskStats) {
                          if (item.type == "completed") {
                            item.info.completed_count = item.info.completed_count - 1;
                            item.info.total_count = item.info.total_count - 1;
                          }
                          if (item.type == "open") {
                            item.info.total_count = item.info.total_count + 1;
                          }
                        }
                        this.databaseService.addTaskStats(taskStats).subscribe(done => {
                          this.getTasksStats();
                        });
                      }
                    }
                  });
                }
              });
            }
          });
        });
      });
    }
    return response.asObservable();
  }

  public updateTaskError(oldTask, err) {
    console.log("TaskRepository updateTaskError", err);

    this.store.dispatch(new UpdateTaskFail());
    if ( err && err.includes(TasksConstants.FOUND_UPDATE_TASK_CONFLICT) ) {
      this.errorService.emit({ id: ErrorType.TaskUpdateFail, messages: this.msgs.FOUND_UPDATE_TASK_CONFLICT_ERROR });
    } else if ( err && err.includes(this.msgs.RESOURCE_NOT_FOUND)) {
      this.store.dispatch(new RemoveTasksSuccess({ ids: [oldTask.id] }));
      this.errorService.emit({ id: ErrorType.TaskUpdateFail, messages: this.msgs.TASK_HAS_BEEN_DELETED });
    } else {
      this.errorService.emit({ id: ErrorType.TaskUpdateFail, messages: err });
    }
  }

  public deleteBulkAttachments(taskId: number, attachments: TaskAttachment[]) {
    if (attachments.length === 0) {
      return;
    }

    let ids = [];
    attachments.forEach(attachment => {
      ids.push(attachment.id);
    });

    if (this.isOnline) {
      this.taskService.deleteBulkAttachments(ids).pipe(take(1)).subscribe( res => {
      }, err => {
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
      });
    } else if (CommonUtil.isSQLSupported()) {
      this.databaseService.addPendingAttachmentsBulkDelete(taskId, ids).subscribe(res => {

      });
    }
  }

  public getStatusItems(): any {
    return [
      { id: TaskUtils.statusNewId(), name: TaskUtils.statusNewName(), displayName: this.msgs.STATUS_NEW },
      { id: TaskUtils.statusInProgressId(), name: TaskUtils.statusInProgressName(), displayName: this.msgs.STATUS_INPROGRESS },
      { id: TaskUtils.statusCompletedId(), name: TaskUtils.statusCompletedName(), displayName: this.msgs.STATUS_COMPLETED, is_closed: true }
    ];
  }
}
